import { Link } from "react-router-dom";
import { CATEGORY_LABELS } from "../lib/constants";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// The kicker is never coloured text — lime only ever appears as a filled block
// with black type sitting on it.
function Kicker({ children, className = "" }) {
    return (
        <span className={`eyebrow inline-block border-2 border-foreground bg-accent px-2 py-1 text-accent-foreground ${className}`}>
            {children}
        </span>
    );
}

export function ArticleCard({ article, variant = "default" }) {
    if (!article) return null;
    const label = CATEGORY_LABELS[article.category] || article.category;

    if (variant === "hero") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`hero-article-${article.id}`}
                className="group block animate-fade-up border-[3px] border-foreground bg-surface shadow-lift hover:translate-x-1 hover:translate-y-1 hover:shadow-card transition-[transform,box-shadow] duration-200"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12">
                    <div className="lg:col-span-7 order-2 lg:order-1 p-6 sm:p-8 flex flex-col">
                        <Kicker>{label}</Kicker>
                        <h2 className="font-display text-[2rem] sm:text-5xl lg:text-[3.75rem] uppercase leading-[0.92] tracking-[-0.02em] mt-5 text-foreground">
                            {article.title}
                        </h2>
                        {article.excerpt && (
                            <p className="mt-6 border-t-[3px] border-foreground pt-4 text-[15px] leading-relaxed text-dim max-w-2xl line-clamp-3">
                                {article.excerpt}
                            </p>
                        )}
                        <div className="mt-auto pt-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-foreground">
                            {article.author && <span className="truncate max-w-[220px]">{article.author}</span>}
                            {article.author && <span className="w-2 h-2 bg-accent border border-foreground" />}
                            <span>{timeAgo(article.published_at)}</span>
                        </div>
                    </div>
                    <div className="lg:col-span-5 order-1 lg:order-2 border-b-[3px] lg:border-b-0 lg:border-l-[3px] border-foreground bg-surface-raised">
                        <div className="aspect-[16/10] lg:h-full overflow-hidden">
                            {article.thumbnail && (
                                <img
                                    src={article.thumbnail}
                                    alt={article.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    if (variant === "compact") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`article-card-${article.id}`}
                className="group flex gap-4 border-2 border-foreground bg-surface p-3 shadow-inset hover:bg-accent-soft hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none transition-[transform,box-shadow,background-color] duration-150"
            >
                <div className="flex-1 min-w-0">
                    <Kicker className="!text-[10px] !px-1.5 !py-0.5">{label}</Kicker>
                    <h3 className="font-display text-[15px] uppercase leading-[1.15] tracking-[-0.01em] mt-2 text-foreground line-clamp-3">
                        {article.title}
                    </h3>
                    <span className="font-mono text-[11px] uppercase tracking-wider text-dim mt-2 block">
                        {timeAgo(article.published_at)}
                    </span>
                </div>
                {article.thumbnail && (
                    <div className="w-[88px] h-[72px] shrink-0 overflow-hidden border-2 border-foreground bg-surface-raised">
                        <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover" />
                    </div>
                )}
            </Link>
        );
    }

    return (
        <Link
            to={`/article/${article.id}`}
            data-testid={`article-card-${article.id}`}
            className="group flex flex-col animate-fade-up border-[3px] border-foreground bg-surface shadow-card hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-[transform,box-shadow] duration-150"
        >
            <div className="aspect-[3/2] overflow-hidden border-b-[3px] border-foreground bg-surface-raised">
                {article.thumbnail ? (
                    <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                    />
                ) : (
                    <div className="w-full h-full bg-surface-raised" />
                )}
            </div>
            <div className="p-5 flex flex-col flex-1">
                <Kicker className="!text-[10px] !px-1.5 !py-0.5">{label}</Kicker>
                <h3 className="font-display text-xl uppercase leading-[1.08] tracking-[-0.015em] mt-3 text-foreground">
                    {article.title}
                </h3>
                {article.excerpt && (
                    <p className="mt-3 text-sm text-dim leading-relaxed line-clamp-3">{article.excerpt}</p>
                )}
                <div className="mt-auto pt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-foreground">
                    {article.author && <span className="truncate max-w-[150px]">{article.author}</span>}
                    {article.author && <span className="w-2 h-2 bg-accent border border-foreground" />}
                    <span>{timeAgo(article.published_at)}</span>
                </div>
            </div>
        </Link>
    );
}

export { timeAgo };
